import React, { useEffect, useState, useMemo } from "react";
import { Pie, Column } from "@ant-design/plots";
import { convertUnitsFinal } from "../../../../utils/dataAndFormulas/wasteForms";
import { useWindowWidth } from "../../../../hooks/useWindowWidth";
import { getDonutHeight, BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { formatWhole, formatMagnitudeAdaptive, formatPercentage } from "../../../../utils/numberFormat";

import "./TableGraf.scss";
import { EmptyChartMessage } from "../EmptyChartMessage";

export const getDisposalLabel = (key) => {
  if (key.startsWith("reuse_recycling")) return "Reutilización / Reciclaje";
  if (key.startsWith("composting")) return "Compostaje";
  if (key.startsWith("energy_recovery")) return "Recuperación Energética";
  if (key.startsWith("incineration_no_energy_recovery")) return "Incineración sin Recuperación";
  if (key.startsWith("landfill")) return "Relleno Sanitario";
  if (key.startsWith("other_methods")) return "Otros Métodos";
  return "Otros";
};

// El label de arriba queda fijo en español porque es la clave interna que
// matchea contra DONUT_COLORS.waste (colores) — el texto que se muestra
// (pills, tooltip, leyenda del PDF) se traduce por separado con este mapa.
// Mismo patrón que ENERGY_GROUP_LABEL_KEYS en EnergyKPIsDashboard/TableGraf.js.
export const DISPOSAL_LABEL_KEYS = {
  "Reutilización / Reciclaje": "disposal_reuse_recycling",
  "Compostaje": "composting",
  "Recuperación Energética": "disposal_energy_recovery",
  "Incineración sin Recuperación": "disposal_incineration",
  "Relleno Sanitario": "disposal_landfill",
  "Otros Métodos": "disposal_other_methods",
  "Otros": "disposal_other",
};

export const convertDisposalUnitsToKg = (value, unit) => {
  if (!value || isNaN(value)) return 0;
  switch (unit) {
    case "kg": return value;
    case "g": return value / 1000;
    case "t":
    default: return value * 1000;
  }
};

// Agrupa totalesMethods (detailPerConcept) por método de disposición base
// para un año dado, convertido a kg — misma lógica que arma WasteDonut
// para su `allData`, extraída para reusar en el PDF.
export function getWasteDisposalTotals(totalesMethods, year) {
  if (!totalesMethods || !year) return [];

  const groupedData = {};
  Object.entries(totalesMethods).forEach(([concept, methods]) => {
    Object.entries(methods).forEach(([methodKey, yearsObj]) => {
      const yearData = yearsObj?.[year];
      if (!yearData || !yearData.total || yearData.total <= 0) return;
      const { total, unit } = yearData;
      const totalKg = convertDisposalUnitsToKg(total, unit);
      if (totalKg <= 0) return;
      const baseMethod = methodKey.replace(/_(rnp|rep)$/, "");
      const label = getDisposalLabel(baseMethod);
      if (!groupedData[baseMethod]) groupedData[baseMethod] = { label, total: 0 };
      groupedData[baseMethod].total += totalKg;
    });
  });

  return Object.values(groupedData)
    .filter(({ total }) => total > 0)
    .map(({ label, total }) => ({ type: label, value: total }));
}

export const buildWasteBarChartData = (data) => {
  if (!data?.total_non_hazardous_waste_kpis) return [];
  return Object.entries(data.total_non_hazardous_waste_kpis)
    .filter(([, { total }]) => total > 0)
    .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA)) // Ordenar descendente
    .slice(0, 5) // Tomar últimos 5 años
    .sort(([yearA], [yearB]) => Number(yearA) - Number(yearB)) // Reordenar ascendente para el gráfico
    .map(([year, { total, unit }]) => ({
      year,
      value: total,
      unit,
    }));
};

export const buildWasteBarChartDataPeligrosos = (data) => {
  if (!data?.total_hazardous_waste_kpis) return [];
  return Object.entries(data.total_hazardous_waste_kpis)
    .filter(([, { total }]) => total > 0)
    .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA)) // Ordenar descendente
    .slice(0, 5) // Tomar últimos 5 años
    .sort(([yearA], [yearB]) => Number(yearA) - Number(yearB)) // Reordenar ascendente para el gráfico
    .map(([year, { total, unit }]) => ({
      year,
      value: total,
      unit,
    }));
};

const TableGraf = ({ filters, totales, t, totalesMethods }) => {
  const { year } = filters || {};
  const [dataConsumo, setDataConsumo] = useState([]);
  const [data, setdata] = useState([]);
  const windowWidth = useWindowWidth();

  // Memoizar configuración responsive para evitar recalcular en cada render
  const responsiveConfig = useMemo(() => {
    const isMobile = windowWidth <= 768;
    const isSmallMobile = windowWidth <= 480;
    const isTablet = windowWidth > 768 && windowWidth <= 992;
    const isNotebook = windowWidth >= 1024 && windowWidth <= 1500;

    return {
      isMobile,
      isSmallMobile,
      isTablet,
      isNotebook,
      // Tamaños de fuente consistentes
      fontSize: {
        large: isSmallMobile ? 12 : isMobile ? 14 : 16,
        medium: isSmallMobile ? 10 : isMobile ? 11 : 12,
        small: isSmallMobile ? 9 : isMobile ? 10 : 11,
      },
      // Alturas de gráficos
      chartHeight: {
        donut: getDonutHeight(isNotebook, isMobile),
        bar: isSmallMobile ? 250 : isMobile ? 280 : 300,
      },
    };
  }, [windowWidth]);

  useEffect(() => {
    if (!totales || !year) return;

    const costoData = totales;

    //setDataConsumo(consumoData);
    setdata(costoData);
  }, [filters, totales, year]);

  const wasteColorMapDonut = DONUT_COLORS.waste;

  const barPalette = BAR_PALETTE;

    ///METODOS DE DISPOSICION/////
  const WasteDonut = ({ detailPerConcept, year, t }) => {
    const [hiddenTypes, setHiddenTypes] = useState([]);

    if (!detailPerConcept || !year) return null;

    const allData = getWasteDisposalTotals(detailPerConcept, year).map(
      ({ type, value }) => ({ label: type, total: value }),
    );
    const chartData = allData.filter(d => !hiddenTypes.includes(d.label));
    const totalVisible = chartData.reduce((acc, { total }) => acc + total, 0);

    //console.log(`📋 chartData para gráfico:`, chartData);

    const config = {
      data: chartData,
      angleField: "total",
      colorField: "label",
      scale: {
        color: {
          domain: Object.keys(wasteColorMapDonut),
          range: Object.values(wasteColorMapDonut),
        },
      },
      radius: 1,
      innerRadius: 0.6,
      height: responsiveConfig.chartHeight.donut,
      label: {
        text: (datum) => {
          const percent = totalVisible > 0 ? (datum?.total / totalVisible) * 100 : 0;
          return percent > 0 ? formatPercentage(percent) : "";
        },
        position: "spider",
        style: {
          fontSize: responsiveConfig.fontSize.small,
          fontWeight: 600,
        },
      },
      legend: false,
      statistic: {
        title: false,
        content: {
          content: `${t("total")}\n${formatMagnitudeAdaptive(totalVisible)} kg`,
          style: {
            fontSize: responsiveConfig.fontSize.large,
            fontWeight: 300,
            whiteSpace: "pre-line",
          },
        },
      },
      tooltip: {
        shared: true,
        title: (datum) => t(DISPOSAL_LABEL_KEYS[datum.label] || datum.label),
        items: [
          (datum) => {
            const percent = totalVisible > 0 ? (datum?.total / totalVisible) * 100 : 0;
            return {
              name: t("total"),
              value: `${formatMagnitudeAdaptive(datum?.total)} kg (${formatPercentage(percent)})`,
            };
          },
        ],
      },
      interactions: [{ type: "element-active" }],
    };

    if (!allData.length) {
      return (
        <EmptyChartMessage
          message={
            t("complete_at_least_one_period") ||
            "Complete al menos un período para visualizar este indicador"
          }
        />
      );
    }

    return (
      <>
        <div className="chartWrapper">
          <Pie {...config} />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            alignItems: "start",
            gap: "8px",
            marginTop: "15px",
            borderTop: "1px solid #000",
            paddingTop: "15px",
            // 130px = mismo min-height que .pillsRow (tableGraf.shared.scss),
            // para que la línea (borderTop) quede a la misma altura que en
            // las cards de barras dentro del mismo chartCard flex-column.
            minHeight: "130px",
          }}
        >
          {allData.map((item) => {
            const isHidden = hiddenTypes.includes(item.label);
            const color = wasteColorMapDonut[item.label] || "#D9D9D9";
            return (
              <div
                key={item.label}
                onClick={() =>
                  setHiddenTypes(prev =>
                    prev.includes(item.label)
                      ? prev.filter(y => y !== item.label)
                      : [...prev, item.label]
                  )
                }
                className="yearPill"
                style={{
                  backgroundColor: isHidden ? "#E5E7EB" : color,
                  color: isHidden ? "#9CA3AF" : "#FFF",
                  padding: "6px 4px",
                  borderRadius: "20px",
                  fontSize: "10px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: `1px solid ${isHidden ? "#D1D5DB" : color}`,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "28px",
                  opacity: isHidden ? 0.7 : 1,
                  textDecoration: isHidden ? "line-through" : "none",
                  userSelect: "none",
                }}
              >
                {t(DISPOSAL_LABEL_KEYS[item.label] || item.label)}
              </div>
            );
          })}
        </div>
      </>
    );
  };


   ///RESIDUOS NO PELIGROSOS/////
  const WasteBarChart = ({ data, t }) => {
    const rawData = buildWasteBarChartData(data);
    const allYears = rawData.map(d => d.year);
    const [activeYears, setActiveYears] = useState(allYears);

    const colorMap = useMemo(() => {
      const map = {};
      allYears.forEach((y, i) => { map[y] = barPalette[i] || "#ccc"; });
      return map;
    }, [allYears]);

    const maxValue = Math.max(...rawData.map(d => d.value));

    const chartData = rawData
      .filter(d => activeYears.includes(d.year))
      .map(d => {
        const isSmall = d.value < 1;
        return {
          year: d.year,
          unit: d.unit,
          displayValue: isSmall ? maxValue * 0.1 : d.value,
          realValue: d.value,
          isSmall,
        };
      });

    const config = {
      data: chartData,
      xField: "year",
      yField: "displayValue",
      autoFit: true,
      colorField: "year",
      scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
      height: responsiveConfig.chartHeight.bar,
      label: false,
      legend: false,
      style: { maxWidth: 120 },
      tooltip: {
        shared: true,
        title: (d) => `${d.year}`,
        items: [
          (datum) => {
            const value = datum?.realValue ?? datum?.value ?? 0;
            const unit = datum?.unit || "";
            return { name: " ", value: `${formatWhole(value)} ${unit}` };
          },
        ],
      },
      // `xAxis`/`yAxis` es la API vieja (v1) de @ant-design/plots — la
      // instalada (v2) no la reconoce, quedaba ignorada en silencio (mismo
      // bug ya visto en GEI/Social/Combustibles/Efluentes). axis.x/axis.y
      // es la forma correcta.
      axis: {
        x: {
          title: t("year"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.medium,
          labelAutoRotate: !responsiveConfig.isMobile,
          labelAutoHide: false,
        },
        y: {
          title: responsiveConfig.isMobile ? t("total") : t("total_waste_axis"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.small,
          labelFormatter: (v) => {
            if (responsiveConfig.isMobile && v >= 1000) return `${(v / 1000).toFixed(1)}K`;
            return formatWhole(v);
          },
        },
      },
      interactions: [{ type: "active-region" }],
    };

    return (
      <>
        <div className="chartWrapper"><Column {...config} /></div>
        <div className="pillsRow">
          {allYears.map(year => (
            <div
              key={year}
              className="yearPill"
              onClick={() => setActiveYears(prev =>
                prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
              )}
              style={{
                backgroundColor: activeYears.includes(year) ? colorMap[year] : "#E5E7EB",
                color: activeYears.includes(year) ? "white" : "#9CA3AF",
                border: `1px solid ${activeYears.includes(year) ? colorMap[year] : "#D1D5DB"}`,
                opacity: activeYears.includes(year) ? 1 : 0.7,
                textDecoration: activeYears.includes(year) ? "none" : "line-through",
                cursor: "pointer",
                transition: "all 0.2s ease",
                userSelect: "none",
              }}
            >
              {year}
            </div>
          ))}
        </div>
      </>
    );
  };

   ///RESIDUOS PELIGROSOS/////
  const WasteBarChartPeligrosos = ({ data, t }) => {
    const rawData = buildWasteBarChartDataPeligrosos(data);
    const allYears = rawData.map(d => d.year);
    const [activeYears, setActiveYears] = useState(allYears);

    const colorMap = useMemo(() => {
      const map = {};
      allYears.forEach((y, i) => { map[y] = barPalette[i] || "#ccc"; });
      return map;
    }, [allYears]);

    const maxValue = Math.max(...rawData.map(d => d.value));

    const chartData = rawData
      .filter(d => activeYears.includes(d.year))
      .map(d => {
        const isSmall = d.value < 1;
        return {
          year: d.year,
          unit: d.unit,
          displayValue: isSmall ? maxValue * 0.1 : d.value,
          realValue: d.value,
          isSmall,
        };
      });

    const config = {
      data: chartData,
      xField: "year",
      yField: "displayValue",
      autoFit: true,
      colorField: "year",
      scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
      height: responsiveConfig.chartHeight.bar,
      label: false,
      legend: false,
      style: { maxWidth: 120 },
      tooltip: {
        shared: true,
        title: (d) => `${d.year}`,
        items: [
          (datum) => {
            const value = datum?.realValue ?? datum?.value ?? 0;
            const unit = datum?.unit || "";
            return { name: " ", value: `${formatWhole(value)} ${unit}` };
          },
        ],
      },
      // Ver comentario en WasteBarChart más arriba: axis.x/axis.y es la
      // API correcta (v2), xAxis/yAxis se ignoraba en silencio.
      axis: {
        x: {
          title: t("year"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.medium,
          labelAutoRotate: !responsiveConfig.isMobile,
          labelAutoHide: false,
        },
        y: {
          title: responsiveConfig.isMobile ? t("total") : t("total_hazardous_waste_axis"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.small,
          labelFormatter: (v) => {
            if (responsiveConfig.isMobile && v >= 1000) return `${(v / 1000).toFixed(1)}K`;
            return formatWhole(v);
          },
        },
      },
      interactions: [{ type: "active-region" }],
    };

    return (
      <>
        <div className="chartWrapper"><Column {...config} /></div>
        <div className="pillsRow">
          {allYears.map(year => (
            <div
              key={year}
              className="yearPill"
              onClick={() => setActiveYears(prev =>
                prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
              )}
              style={{
                backgroundColor: activeYears.includes(year) ? colorMap[year] : "#E5E7EB",
                color: activeYears.includes(year) ? "white" : "#9CA3AF",
                border: `1px solid ${activeYears.includes(year) ? colorMap[year] : "#D1D5DB"}`,
                opacity: activeYears.includes(year) ? 1 : 0.7,
                textDecoration: activeYears.includes(year) ? "none" : "line-through",
                cursor: "pointer",
                transition: "all 0.2s ease",
                userSelect: "none",
              }}
            >
              {year}
            </div>
          ))}
        </div>
      </>
    );
  };

  /////////////Barras costo
  const buildBarChartDataCost = (data) => {
    if (!data?.energy_cost_kpis) return [];
    //energy_cost_kpis
    return Object.entries(data.energy_cost_kpis).map(
      ([year, { total, unit }]) => ({
        year,
        total_energy_kpis: total,
        unit,
      })
    );
  };

  const EnergyBarChartCost = ({ data }) => {
    const chartData = buildBarChartDataCost(data);

    const config = {
      data: chartData,
      xField: "year",
      yField: "energy_cost_kpis",
      width: 200,
      height: 300,
      label: {
        position: "top",
        content: ({ energy_cost_kpis, unit }) => `${energy_cost_kpis} ${unit}`,
        style: { fontSize: 12 },
      },
      tooltip: {
        formatter: ({ energy_cost_kpis, unit }) => ({
          name: "Total Energía",
          value: `${energy_cost_kpis} ${unit}`,
        }),
      },
      xAxis: {
        title: { text: "Año" },
      },
      yAxis: {
        title: { text: "Total Energía" },
      },
      interactions: [{ type: "active-region" }],
    };

    return <Column {...config} />;
  };

  // ======================
  // 🎨 RENDER
  // ======================

  return (
    <div className="wasteDashboardGrid">
      <div className="chartCard">
        <h3 className="cardTitle">{t("non_hazardous_waste")}</h3>
        <WasteBarChart data={data} t={t} />
      </div>

      <div className="chartCard">
        <h3 className="cardTitle">{t("hazardous_waste")}</h3>
        <WasteBarChartPeligrosos data={data} t={t} />
      </div>

      <div className="chartCard">
        <h3 className="cardTitle">{t("disposalMethods")}</h3>
        <WasteDonut detailPerConcept={totalesMethods} year={filters.year} t={t} />
      </div>
    </div>
  );
};

export default TableGraf;
